"use client";

import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { VersionedTransaction } from "@solana/web3.js";
import { base58 } from "@scure/base";
import { useMemo } from "react";

export interface SolanaSigner {
  address: string;
  signMessage?: (message: Uint8Array) => Promise<Uint8Array>;
  signTransaction: (transactionBytes: Uint8Array) => Promise<Uint8Array>;
  signAndSendTransaction: (transactionBytes: Uint8Array) => Promise<Uint8Array>;
}

export const useSolanaWallet = (): { signer: SolanaSigner | null } => {
  const {
    connected,
    publicKey,
    sendTransaction,
    signMessage,
    signTransaction,
  } = useWallet();
  const { connection } = useConnection();

  const signer = useMemo<SolanaSigner | null>(() => {
    if (!connected || publicKey === null || signTransaction === undefined) {
      return null;
    }

    return {
      address: publicKey.toBase58(),
      signMessage,
      signTransaction: async (transactionBytes) => {
        const transaction = VersionedTransaction.deserialize(transactionBytes);
        const signed = await signTransaction(transaction);
        return signed.serialize();
      },
      signAndSendTransaction: async (transactionBytes) => {
        const transaction = VersionedTransaction.deserialize(transactionBytes);
        const signature = await sendTransaction(transaction, connection);
        return base58.decode(signature);
      },
    };
  }, [
    connected,
    connection,
    publicKey,
    sendTransaction,
    signMessage,
    signTransaction,
  ]);

  return { signer };
};
